import { cn } from "@/lib/utils";
import { ChevronDown, Search } from "lucide-react";
import * as React from "react";
import { Label } from "./label";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps
  extends Omit<React.ComponentProps<"div">, "value" | "onChange"> {
  options: MultiSelectOption[];
  placeholder?: string;
  value?: string[];
  name?: string;
  label?: string;
  required?: boolean;
  onChange?: (values: string[]) => void;
  searchPlaceholder?: string;
  searchable?: boolean;
}

const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      className,
      options,
      label,
      required,
      id,
      placeholder = "Select options",
      searchPlaceholder = "Search...",
      value = [],
      onChange,
      searchable = false,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [internalValues, setInternalValues] = React.useState(value);
    const [searchQuery, setSearchQuery] = React.useState("");
    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      if (value !== undefined) {
        setInternalValues(value);
      }
    }, [value]);

    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const handleToggle = () => setIsOpen(!isOpen);

    const handleSelect = (newValue: string) => {
      let newValues: string[];
      if (internalValues.includes(newValue)) {
        newValues = internalValues.filter((v) => v !== newValue);
      } else {
        newValues = [...internalValues, newValue];
      }
      setInternalValues(newValues);
      if (onChange) {
        onChange(newValues);
      }
    };

    const selectedOptions = options.filter((option) =>
      internalValues.includes(option.value)
    );

    const filteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <div className={cn("relative", className)} ref={containerRef} {...props}>
        {label && (
          <Label htmlFor={id}>
            {label} {required && <span className="text-error">*</span>}
          </Label>
        )}
        <div
          className="flex items-center justify-between p-2 border rounded-md cursor-pointer"
          onClick={handleToggle}
          id={id}
        >
          <span className="truncate">
            {selectedOptions.length > 0
              ? selectedOptions.map((option) => option.label).join(", ")
              : placeholder}
          </span>
          <ChevronDown className="ml-2" />
        </div>

        {isOpen && (
          <div className="absolute z-10 mt-2 max-h-60 w-full overflow-auto border-border bg-card-bg rounded-md shadow-lg">
            {searchable && (
              <div className="sticky z-20 top-0 bg-card-bg p-2 border-b">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="w-full pl-8 pr-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-green-primary"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}
            {(searchable ? filteredOptions : options).map((option) => (
              <div
                key={option.value}
                className="flex items-center p-3 cursor-pointer hover:bg-background"
                onClick={() => handleSelect(option.value)}
              >
                <input
                  type="checkbox"
                  checked={internalValues.includes(option.value)}
                  onChange={() => handleSelect(option.value)}
                  className="mr-2 bg-background accent-green-primary transform scale-125"
                />
                <span>{option.label}</span>
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div className="p-3 text-center text-muted-foreground">
                No results found
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

MultiSelect.displayName = "MultiSelect";

export { MultiSelect };
